/* ---------- Kitchen elevation, drawn in inches at 3/8 scale ---------- */
const S = 4.8, X0 = 110, FY = 500;
const X = (i) => X0 + S * i;
const Y = (i) => FY - S * i;

function Box({ x1, x2, y1, y2, c, r = 0 }) {
  return <rect className={c} x={X(x1)} y={Y(y2)} width={S * (x2 - x1)} height={S * (y2 - y1)} rx={r} />;
}

function Door({ x1, x2, y1, y2, side, upper }) {
  const px = side === "r" ? x2 - 3.6 : x1 + 3.0;
  const py = upper ? y1 + 3 : y2 - 10;
  return (
    <g>
      <Box x1={x1} x2={x2} y1={y1} y2={y2} c="cab s" />
      <Box x1={x1 + 2.4} x2={x2 - 2.4} y1={y1 + 2.4} y2={y2 - 2.4} c="inset s" />
      <Box x1={px} x2={px + 0.6} y1={py} y2={py + 7} c="brass s" />
    </g>
  );
}

function Drawer({ x1, x2, y1, y2 }) {
  const cx = (x1 + x2) / 2, cy = (y1 + y2) / 2;
  return (
    <g>
      <Box x1={x1} x2={x2} y1={y1} y2={y2} c="cab s" />
      <Box x1={x1 + 2.4} x2={x2 - 2.4} y1={y1 + 2} y2={y2 - 2} c="inset s" />
      <Box x1={cx - 4} x2={cx + 4} y1={cy - 0.3} y2={cy + 0.3} c="brass s" />
    </g>
  );
}

function Dims() {
  const pts = [0, 36, 66, 102, 150, 186, 204];
  const yc = Y(0) + 28, yo = Y(0) + 52;
  const xv = X(204) + 56;
  const hs = [0, 36, 54, 96];
  const vlabels = ["3′-0″", "1′-6″", "3′-6″"];
  const tick = (x, y, k) => <line key={k} x1={x - 5} y1={y + 5} x2={x + 5} y2={y - 5} />;
  return (
    <g className="dims">
      <text className="ttl" x="600" y="24" textAnchor="middle">Kitchen, north wall elevation. Scale 3/8″ = 1′-0″</text>
      <line className="cl" x1={X(126)} x2={X(126)} y1={Y(96) - 10} y2={Y(0) + 14} />
      <line className="cl" x1={X(51)} x2={X(51)} y1={Y(96) - 10} y2={Y(0) + 14} />
      {/* chain dimensions along the floor */}
      {pts.map((p, i) => <line key={"e" + i} x1={X(p)} x2={X(p)} y1={Y(0) + 6} y2={yc + 7} />)}
      <line x1={X(0)} x2={X(204)} y1={yc} y2={yc} />
      {pts.map((p, i) => tick(X(p), yc, "t" + i))}
      {pts.slice(1).map((p, i) => (
        <text key={"l" + i} x={(X(pts[i]) + X(p)) / 2} y={yc - 6} textAnchor="middle">{p - pts[i]}″</text>
      ))}
      {/* overall */}
      <line x1={X(0)} x2={X(0)} y1={yc + 7} y2={yo + 7} />
      <line x1={X(204)} x2={X(204)} y1={yc + 7} y2={yo + 7} />
      <line x1={X(0)} x2={X(204)} y1={yo} y2={yo} />
      {tick(X(0), yo, "o1")}{tick(X(204), yo, "o2")}
      <text x={(X(0) + X(204)) / 2} y={yo - 6} textAnchor="middle">17′-0″</text>
      {/* heights on the right */}
      {hs.map((h, i) => <line key={"h" + i} x1={X(204) + 6} x2={xv + 7} y1={Y(h)} y2={Y(h)} />)}
      <line x1={xv} x2={xv} y1={Y(96)} y2={Y(0)} />
      {hs.map((h, i) => tick(xv, Y(h), "v" + i))}
      {vlabels.map((t, i) => (
        <text key={"v" + i} x={xv - 7} y={(Y(hs[i]) + Y(hs[i + 1])) / 2 + 4} textAnchor="end">{t}</text>
      ))}
    </g>
  );
}

export function Room({ built }) {
  return (
    <g>
      {built && (
        <g>
          <rect className="k-ceil" x="0" y="0" width="1200" height={Y(96)} />
          <rect className="k-wall" x="0" y={Y(96)} width="1200" height={S * 96} />
          <rect className="k-floor" x="0" y={FY} width="1200" height="60" />
          <rect className="k-crown" x="0" y={Y(96)} width="1200" height={S * 4} />
        </g>
      )}
      <Box x1={0} x2={204} y1={0} y2={96} c="wall s" />
      {built && <Box x1={0} x2={204} y1={36} y2={54} c="tile" />}
      {built && <Box x1={0} x2={204} y1={36} y2={54} c="glow" />}

      {/* window over the sink */}
      <Box x1={103} x2={149} y1={42.5} y2={44} c="trim s" />
      <Box x1={105} x2={147} y1={44} y2={84} c="trim s" />
      <Box x1={107} x2={145} y1={46} y2={82} c="glass s" />
      <line className="mull" x1={X(119.67)} x2={X(119.67)} y1={Y(82)} y2={Y(46)} />
      <line className="mull" x1={X(132.33)} x2={X(132.33)} y1={Y(82)} y2={Y(46)} />
      <line className="mull" x1={X(107)} x2={X(145)} y1={Y(64)} y2={Y(64)} />

      {/* upper cabinets */}
      <Door x1={0} x2={18} y1={54} y2={84} side="r" upper />
      <Door x1={18} x2={36} y1={54} y2={84} side="l" upper />
      <Door x1={66} x2={84} y1={54} y2={84} side="r" upper />
      <Door x1={84} x2={102} y1={54} y2={84} side="l" upper />
      <Door x1={150} x2={168} y1={54} y2={84} side="r" upper />
      <Door x1={168} x2={186} y1={54} y2={84} side="l" upper />
      <Door x1={186} x2={204} y1={54} y2={84} side="l" upper />

      {/* hood */}
      <Box x1={42} x2={60} y1={66} y2={92} c="steel s" />
      <polygon className="steel s" points={`${X(36)},${Y(54)} ${X(66)},${Y(54)} ${X(60)},${Y(66)} ${X(42)},${Y(66)}`} />

      {/* base cabinets */}
      <Box x1={0} x2={204} y1={0} y2={4} c="kick s" />
      <Drawer x1={0} x2={36} y1={4} y2={14.2} />
      <Drawer x1={0} x2={36} y1={14.2} y2={24.3} />
      <Drawer x1={0} x2={36} y1={24.3} y2={34.5} />
      <Door x1={66} x2={84} y1={4} y2={28} side="r" />
      <Door x1={84} x2={102} y1={4} y2={28} side="l" />
      <Drawer x1={66} x2={102} y1={28} y2={34.5} />
      <Door x1={102} x2={126} y1={4} y2={28} side="r" />
      <Door x1={126} x2={150} y1={4} y2={28} side="l" />
      <Drawer x1={102} x2={150} y1={28} y2={34.5} />
      <Drawer x1={150} x2={186} y1={4} y2={14.2} />
      <Drawer x1={150} x2={186} y1={14.2} y2={24.3} />
      <Drawer x1={150} x2={186} y1={24.3} y2={34.5} />
      <Door x1={186} x2={204} y1={4} y2={34.5} side="l" />

      {/* counters */}
      <Box x1={0} x2={36} y1={34.5} y2={36} c="counter s" />
      <Box x1={66} x2={204} y1={34.5} y2={36} c="counter s" />

      {/* range */}
      <Box x1={36} x2={66} y1={0.5} y2={36} c="steel s" />
      <Box x1={38} x2={64} y1={6} y2={29} c="steel s" />
      <Box x1={41} x2={61} y1={10} y2={25} c="oven s" />
      <Box x1={40} x2={62} y1={26.4} y2={27.4} c="hw s" />
      {[41.5, 47.5, 54.5, 60.5].map((k) => (
        <circle key={k} className="hw s" cx={X(k)} cy={Y(32.5)} r={S * 1.3} />
      ))}

      {/* faucet */}
      <path className="faucet" d={`M${X(126)} ${Y(36)} V${Y(36) - 30} q0 -14 14 -14 h9 v8`} />

      {built && (
        <g>
          <Box x1={42} x2={54} y1={36} y2={45} c="pot s" r={4} />
          <Box x1={41.4} x2={54.6} y1={45} y2={46.2} c="pot s" />
          <circle className="pot" cx={X(48)} cy={Y(46.2) - 3} r={S * 0.8} />
          <Box x1={139} x2={144} y1={36} y2={43} c="vase s" r={6} />
          <line className="stem" x1={X(141.5)} y1={Y(43)} x2={X(136.5)} y2={Y(57)} />
          <line className="stem" x1={X(141.5)} y1={Y(43)} x2={X(141.8)} y2={Y(61)} />
          <line className="stem" x1={X(141.5)} y1={Y(43)} x2={X(147)} y2={Y(56)} />
          <circle className="bloom" cx={X(136.5)} cy={Y(57)} r={S * 2} />
          <circle className="bloom" cx={X(141.8)} cy={Y(61)} r={S * 2} />
          <circle className="bloom" cx={X(147)} cy={Y(56)} r={S * 2} />
        </g>
      )}

      <Box x1={0} x2={204} y1={92} y2={96} c="trim s" />
      {!built && <Dims />}
    </g>
  );
}
